export const authEndpoints = {
  login: '/auth/login' as const,
  externalLogin: '/auth/external-login' as const,
  logout: '/auth/logout' as const,

  register: '/auth/register' as const,
  refreshToken: '/auth/refresh' as const,
  token: '/auth/token' as const,
};

export const trialOfferParamKey = 'offer';
export const trialOfferFailureMessage =
  'We couldn’t apply this trial offer. Ask for a new link.';

export const isInvalidTrialOfferError = (error: unknown) =>
  error instanceof Error &&
  error.message === 'This trial offer is invalid or expired.';

// The API rejects a first-time sign-up from a personal account (e.g. Gmail)
// with this RFC 9457 problem type, on both the sign-up and login pages.
const businessEmailRequiredProblemType =
  'https://finlay.ai/errors/business-email-required';
export const businessEmailRequiredMessage =
  'Sign up with your business email. If you don’t have one, contact dev@finlay.ai.';

export const isBusinessEmailRequiredError = (error: unknown) =>
  error instanceof Error &&
  (error as { type?: unknown }).type === businessEmailRequiredProblemType;

export const authMethods = {
  google: 'Google' as const,
  email: 'Email' as const,
  linkedin: 'LinkedIn' as const,
  slack: 'Slack' as const,
} as const;

export type AuthMethod = (typeof authMethods)[keyof typeof authMethods];

export type AuthFailureReason =
  | 'business_email_required'
  | 'invalid_trial_offer'
  | 'invalid_credentials'
  | 'linkedin_sso_error'
  | 'slack_sso_error'
  | 'google_sso_error'
  | 'provider_error'
  | 'unknown';

/**
 * Buckets a failed sign-up or sign-in for `signUpFailed`/`signInFailed`.
 * A missing error means the provider failed before our API saw the request.
 */
export const getAuthFailureReason = (
  error: unknown,
  authMethod: AuthMethod,
): AuthFailureReason => {
  if (isBusinessEmailRequiredError(error)) return 'business_email_required';
  if (isInvalidTrialOfferError(error)) return 'invalid_trial_offer';
  // The login API answers a wrong email or password with 403 "Invalid email
  // or password" (401 when unauthenticated); 400 is a validation failure the
  // form already prevents, so it isn't counted as bad credentials.
  const status = (error as { status?: unknown } | undefined)?.status;
  if (authMethod === 'Email' && (status === 401 || status === 403)) {
    return 'invalid_credentials';
  }
  if (!error) return 'provider_error';
  return 'unknown';
};
